class FinalB2BBulkOrderForm {
  constructor() {
    this.allProducts = [];
    this.filteredProducts = [];
    this.displayedProductCount = 0;
    this.isLoading = false;
    this.searchTerm = '';
    this.selectedCollections = ['all'];
    this.selectedAvailability = [];
    this.isSearchMode = false;
    this.productCache = new Map();
    this.searchCache = new Map();
    this.maxLoadedShopifyPage = 0;
    window.inventoryDetailsData = window.inventoryDetailsData || {};
    window.incomingInventoryData = window.incomingInventoryData || {};
    this.config = JSON.parse(document.getElementById('storeConfig').textContent);
    this.productsPerPage = this.config.products_per_page;
    this.hideRenegadeItems = this.config.hide_renegade_items || false;
    
    // Debug logging
    // console.log('DEBUG: Constructor - hideRenegadeItems:', this.hideRenegadeItems);
    // console.log('DEBUG: Constructor - config:', this.config);
    
    this.init();
  }

  async init() {
    this.bindEvents();
    await this.loadInitialProducts();
    this.applyFiltersAndSort();
    this.renderCurrentPage();
    this.renderPagination();
    this.updateCollectionCountsDisplay();
    this.hideLoading();
  }

  async loadInitialProducts() {
    try {
      const initialDataElement = document.getElementById('initialProductsData');
      if (!initialDataElement) {
        console.error('Error: #initialProductsData script tag not found.');
        this.allProducts = [];
        return;
      }
      const initialDataText = initialDataElement.textContent;
      const initialData = JSON.parse(initialDataText);
      
      // The Liquid template already filters out renegade products, so we don't need to filter again here
      // This preserves the initial loading functionality
      this.allProducts = initialData.products || [];
      
      // console.log('DEBUG: Initial products loaded:', this.allProducts.length);
      // console.log('DEBUG: Sample product custom_fields:', this.allProducts[0]?.custom_fields);
      
      this.allProducts.forEach(product => {
        this.productCache.set(product.variant_id, product);
        if (product.custom_fields.inventory_by_location) {
          try {
            window.inventoryDetailsData[product.variant_id] = typeof product.custom_fields.inventory_by_location === 'string' ?
              JSON.parse(product.custom_fields.inventory_by_location) :
              product.custom_fields.inventory_by_location;
          } catch (e) {
            console.warn('Error parsing initial inventory_by_location for product', product.variant_id, e);
            window.inventoryDetailsData[product.variant_id] = {};
          }
        } else {
          window.inventoryDetailsData[product.variant_id] = {};
        }
        if (product.custom_fields.incoming_inventory) {
          try {
            const temp = typeof product.custom_fields.incoming_inventory === 'string' ?
              JSON.parse(product.custom_fields.incoming_inventory) :
              product.custom_fields.incoming_inventory;
            if (temp && typeof temp === 'object' && temp.error) {
              console.warn('Shopify Liquid outputted an error for incoming_inventory:', temp.error, 'for variant', product.variant_id);
              window.incomingInventoryData[product.variant_id] = [];
            } else {
              window.incomingInventoryData[product.variant_id] = temp;
            }
          } catch (e) {
            console.warn('Error parsing initial incoming_inventory for product', product.variant_id, e);
            window.incomingInventoryData[product.variant_id] = [];
          }
        } else {
          window.incomingInventoryData[product.variant_id] = [];
        }
      });
      this.maxLoadedShopifyPage = 1;
    } catch (error) {
      console.error('Error loading initial products from #initialProductsData:', error);
      this.allProducts = [];
    }
  }

  shouldHideRenegadeProduct(product) {
    if (!this.hideRenegadeItems) {
      // console.log('DEBUG: shouldHideRenegadeProduct - hideRenegadeItems is false, allowing product:', product.title);
      return false;
    }
    
    const customFields = product.custom_fields || {};
    const renegadeValue = customFields.renegade_product;
    const isRenegade = renegadeValue === true || renegadeValue === 'true' || renegadeValue === 1;
    
    // console.log('DEBUG: shouldHideRenegadeProduct - Product:', product.title, 'SKU:', product.sku);
    // console.log('DEBUG: shouldHideRenegadeProduct - Raw renegade_product value:', renegadeValue, 'Type:', typeof renegadeValue);
    // console.log('DEBUG: shouldHideRenegadeProduct - Evaluated as renegade:', isRenegade);
    // console.log('DEBUG: shouldHideRenegadeProduct - Will hide:', isRenegade);
    
    return isRenegade;
  }

  bindEvents() {
    let searchTimeout;
    document.getElementById('productSearch').addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      this.searchTerm = e.target.value.toLowerCase().trim();
      if (this.searchTerm.length >= 2) {
        searchTimeout = setTimeout(() => {
          this.performSearch();
          const start = Math.min(1, this.filteredProducts.slice(0, this.displayedProductCount).length);
          const end = this.filteredProducts.slice(0, this.displayedProductCount).length;
          const total = this.filteredProducts.length;
          let infoText = '';
          if (total === 0) {
            infoText = 'No products found.';
          } else {
            infoText = `Showing ${start}-${end} of ${total} products`;
          }
          document.getElementById('resultsInfo').textContent = infoText;
        }, 300);
      } else if (this.searchTerm.length === 0) {
        this.clearSearch();
      }
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.search-container')) {
        document.getElementById('searchSuggestions').style.display = 'none';
      }
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.inventory-cell-wrapper') &&
        !event.target.closest('.inventory-details-row') &&
        !event.target.closest('.inventory-toggle-btn')) {
        document.querySelectorAll('.inventory-details-row.show').forEach(row => {
          row.classList.remove('show');
        });
        document.querySelectorAll('.inventory-toggle-btn.active').forEach(btn => {
          btn.classList.remove('active');
          btn.textContent = '+';
        });
      }
    });
  }

  async performSearch() {
    if (this.searchTerm.length < 2) {
      this.clearSearch();
      return;
    }

    // console.log('DEBUG: ===== STARTING SEARCH =====');
    // console.log('DEBUG: Search term:', this.searchTerm);
    // console.log('DEBUG: hideRenegadeItems setting:', this.hideRenegadeItems);
    
    this.isSearchMode = true;
    this.showPageLoading();

    const searchTermLower = this.searchTerm.toLowerCase().trim();

    // CHECKPOINT 1: Local exact SKU search with aggressive filtering
    // console.log('DEBUG: CHECKPOINT 1 - Local exact SKU search');
    const exactSkuMatchLocal = this.allProducts.find((product) => {
      const skuMatch = product.sku && product.sku.toLowerCase().trim() === searchTermLower;
      const shouldHide = this.shouldHideRenegadeProduct(product);
      
      if (skuMatch) {
        // console.log('DEBUG: Found SKU match:', product.title, 'SKU:', product.sku, 'Should hide:', shouldHide, 'Renegade value:', product.custom_fields?.renegade_product);
      }
      
      return skuMatch && !shouldHide;
    });

    if (exactSkuMatchLocal) {
      // console.log('DEBUG: Returning exact local SKU match:', exactSkuMatchLocal.title);
      this.filteredProducts = [exactSkuMatchLocal];
      this.displayedProductCount = 1;
      this.renderSearchResults();
      this.hidePageLoading();
      return;
    }

    // CHECKPOINT 2: Local partial search with aggressive filtering
    // console.log('DEBUG: CHECKPOINT 2 - Local partial search');
    const localResults = this.allProducts.filter((product) => {
      const shouldHide = this.shouldHideRenegadeProduct(product);
      
      if (shouldHide) {
        // console.log('DEBUG: CHECKPOINT 2 - Filtering out renegade product:', product.title, 'SKU:', product.sku);
        return false;
      }
      
      const productSkuLower = product.sku ? product.sku.toLowerCase().trim() : "";
      const productTitleLower = product.title.toLowerCase();
      const productVariantOptionsLower = product.variant_options ? product.variant_options.toLowerCase() : "";

      const matches = productTitleLower.includes(searchTermLower) ||
         productSkuLower.includes(searchTermLower) ||
         productVariantOptionsLower.includes(searchTermLower);
       
      if (matches) {
        // console.log('DEBUG: CHECKPOINT 2 - Local match found:', product.title, 'SKU:', product.sku, 'Renegade:', product.custom_fields?.renegade_product);
      }
      
      return matches;
    });

    // console.log('DEBUG: CHECKPOINT 2 - Local results count:', localResults.length);

    try {
      // CHECKPOINT 3: API search
      // console.log('DEBUG: CHECKPOINT 3 - Starting API search');
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(this.searchTerm)}&resources[type]=product&resources[limit]=50`,
      );
      const data = await response.json();
      let apiResults = [];

      if (data.resources && data.resources.results && data.resources.results.products) {
        const products = data.resources.results.products.filter((p) => !p.title.toLowerCase().includes("sample"));
        // console.log('DEBUG: CHECKPOINT 3 - API returned products:', products.length);
    
        const productHandlesToFetch = products.map((p) => p.handle);
        const fullProductDataPromises = productHandlesToFetch.map((handle) =>
          this.fetchProductWithFullData(handle, true),
        );
        const allFullProductDataResults = await Promise.all(fullProductDataPromises);
    
        allFullProductDataResults.forEach((fullProductData) => {
          if (fullProductData.length > 0) {
            fullProductData.forEach(productEntry => {
              // console.log('DEBUG: CHECKPOINT 3 - Processing API product:', productEntry.title, 'SKU:', productEntry.sku, 'Renegade:', productEntry.custom_fields?.renegade_product);
            
              // Add to allProducts cache for future use
              if (!this.productCache.has(productEntry.variant_id)) {
                this.allProducts.push(productEntry);
                this.productCache.set(productEntry.variant_id, productEntry);
              } else {
                this.productCache.set(productEntry.variant_id, productEntry);
                const indexInAllProducts = this.allProducts.findIndex(p => p.variant_id === productEntry.variant_id);
                if (indexInAllProducts !== -1) {
                  this.allProducts[indexInAllProducts] = productEntry;
                }
              }
          
              // CRITICAL FILTER: Only add to search results if not a renegade product
              const shouldHideFromAPI = this.shouldHideRenegadeProduct(productEntry);
              if (!shouldHideFromAPI) {
                apiResults.push(productEntry);
                // console.log('DEBUG: CHECKPOINT 3 - Added API product to results:', productEntry.title, 'SKU:', productEntry.sku);
              } else {
                // console.log('DEBUG: CHECKPOINT 3 - BLOCKED renegade product from API:', productEntry.title, 'SKU:', productEntry.sku);
              }
            });
          }
        });
      }

      // console.log('DEBUG: CHECKPOINT 3 - API results after filtering:', apiResults.length);

      // CHECKPOINT 4: API exact SKU match (from already filtered results)
      // console.log('DEBUG: CHECKPOINT 4 - API exact SKU check');
      const exactSkuMatchApi = apiResults.find(
        (product) => product.sku && product.sku.toLowerCase().trim() === searchTermLower,
      );

      if (exactSkuMatchApi) {
        // console.log('DEBUG: CHECKPOINT 4 - Found exact API SKU match:', exactSkuMatchApi.title);
        this.filteredProducts = [exactSkuMatchApi];
        this.displayedProductCount = 1;
        this.renderSearchResults();
        this.hidePageLoading();
        return;
      }

      // CHECKPOINT 5: Combine results (both already filtered)
      // console.log('DEBUG: CHECKPOINT 5 - Combining results');
      let combinedResults = [...localResults];
      apiResults.forEach((apiProduct) => {
        if (!combinedResults.find((p) => p.variant_id === apiProduct.variant_id)) {
          combinedResults.push(apiProduct);
        }
      });

      // console.log('DEBUG: CHECKPOINT 5 - Combined results count:', combinedResults.length);
  
      // CHECKPOINT 6: Final safety filter (triple check)
      // console.log('DEBUG: CHECKPOINT 6 - Final safety filter');
      const finalResults = combinedResults.filter(product => {
        const shouldHide = this.shouldHideRenegadeProduct(product);
        if (shouldHide) {
          // console.log('DEBUG: CHECKPOINT 6 - FINAL BLOCK of renegade product:', product.title, 'SKU:', product.sku);
          return false;
        }
        return true;
      });
  
      // console.log('DEBUG: CHECKPOINT 6 - Final results count:', finalResults.length);
      // console.log('DEBUG: ===== SEARCH COMPLETE =====');
  
      this.searchCache.set(this.searchTerm, finalResults);
      this.displaySearchResults(finalResults);

    } catch (error) {
      console.error("Global search error:", error);
      // Fallback to local search with filtering
      // console.log('DEBUG: Falling back to local results:', localResults.length);
      this.displaySearchResults(localResults);
    }
    this.hidePageLoading();
  }

  renderSearchResults() {
    const pageProducts = this.filteredProducts.slice(0, this.displayedProductCount);
    const tbody = document.getElementById('productsTableBody');
    let html = '';
    
    if (pageProducts.length === 0 && this.filteredProducts.length === 0 && !this.isLoading) {
      html = `<tr><td colspan="8" style="text-align: center; padding: 20px;">No products found matching your criteria.</td></tr>`;
    } else {
      pageProducts.forEach(product => {
        html += this.renderProductRow(product);
        html += this.renderInventoryDetailsRow(product);
      });
    }
    
    tbody.innerHTML = html;
    this.renderPagination();
    this.updateCollectionCountsDisplay();
    
    // Show search suggestions for the first few results
    this.showSearchSuggestions(this.filteredProducts.slice(0, 10));
    
    // If exact match, update search input
    if (this.filteredProducts.length === 1) {
      const product = this.filteredProducts[0];
      const searchTermLower = this.searchTerm.toLowerCase().trim();
      const productSkuLower = product.sku ? product.sku.toLowerCase().trim() : "";
      if (productSkuLower === searchTermLower) {
        document.getElementById("productSearch").value = product.sku;
      }
    }
  }

  async fetchProductWithFullData(handle, isNewProductFromSearch = false) {
    try {
      let product = null;
      if (!isNewProductFromSearch) {
        product = this.allProducts.find(p => p.handle === handle);
        if (product && product.custom_fields && product.custom_fields.flat_rate_shipping !== null) {
          return [product];
        }
      }

      if (!product) {
        const productResponse = await fetch(`/products/${handle}.js`);
        product = await productResponse.json();
        if (!product || !product.variants) {
          console.warn('No product or variants found for handle:', handle);
          return [];
        }
        if (product.title.toLowerCase().includes('sample')) {
          return [];
        }
      }

      const productEntries = [];
      for (const variant of product.variants) {
        const metafieldsResponse = await fetch(`/products/${handle}?view=metafields-json&variant=${variant.id}`);
        let metafieldsData = {};
        try {
          const metafieldsText = await metafieldsResponse.text();
          const jsonMatch = metafieldsText.match(/<script type="application\/json" id="product-metafields-json">(.*?)<\/script>/s);
          if (jsonMatch && jsonMatch[1]) {
            metafieldsData = JSON.parse(jsonMatch[1]);
          } else if (metafieldsText.trim().startsWith('{')) {
            metafieldsData = JSON.parse(metafieldsText);
          } else {
            console.warn('No valid JSON found in metafields response for', handle, 'variant', variant.id, 'Response starts with:', metafieldsText.trim().substring(0, 200));
          }
        } catch (e) {
          console.warn('Error parsing metafields JSON for', handle, 'variant', variant.id, e);
        }

        let variantOptions = '';
        if (variant.option1 && variant.option1 !== 'Default Title') {
          variantOptions = variant.option1;
          if (variant.option2 && variant.option2 !== 'Default Title') {
            variantOptions += ' / ' + variant.option2;
            if (variant.option3 && variant.option3 !== 'Default Title') {
              variantOptions += ' / ' + variant.option3;
            }
          }
        }

        const currentProductMetafields = metafieldsData?.metafields?.custom || {};
        const currentVariantMetafields = metafieldsData?.metafields?.variant_custom || {};
        console.log("currentVariantMetafields", currentVariantMetafields);
        const flatRateShipping = currentVariantMetafields.flat_rate_shipping || currentProductMetafields.flat_rate_shipping || null;
        const inventoryByLocation = currentVariantMetafields.inventory_by_location || currentProductMetafields.inventory_by_location || null;
        const backorderDate = currentVariantMetafields.estimated_restock_date || currentProductMetafields.estimated_restock_date || null;
        const incomingInventory = currentVariantMetafields.incomming_inventory || currentProductMetafields.incomming_inventory || null;
        
        // Determine renegade status based on product structure
        const renegadeProduct = product.variants.length === 1 ? 
          (currentProductMetafields.renegade_product || false) : 
          (currentVariantMetafields.renegade_product || false);
        
        // console.log('DEBUG: fetchProductWithFullData - Product:', product.title, 'Variant:', variant.id, 'Renegade:', renegadeProduct);
        // console.log('DEBUG: fetchProductWithFullData - All product metafields:', currentProductMetafields);
        // console.log('DEBUG: fetchProductWithFullData - All variant metafields:', currentVariantMetafields);
        // console.log('DEBUG: fetchProductWithFullData - Raw metafields data:', metafieldsData);
        
        let parsedInventoryByLocation = {};
        if (inventoryByLocation) {
          try {
            const temp = typeof inventoryByLocation === 'string' ? JSON.parse(inventoryByLocation) : inventoryByLocation;
            if (temp && typeof temp === 'object' && temp.error) {
              console.warn('Shopify Liquid outputted an error for inventory_by_location:', temp.error, 'for variant', variant.id);
              parsedInventoryByLocation = {};
            } else {
              parsedInventoryByLocation = temp;
            }
          } catch (e) {
            console.warn('Error parsing inventory_by_location for variant', variant.id, ':', e, 'Raw data:', inventoryByLocation);
            parsedInventoryByLocation = {};
          }
        }
        window.inventoryDetailsData[variant.id] = parsedInventoryByLocation;

        let parsedIncomingInventory = [];
        if (incomingInventory) {
          try {
            const temp = typeof incomingInventory === 'string' ? JSON.parse(incomingInventory) : incomingInventory;
            if (temp && typeof temp === 'object' && temp.error) {
              console.warn('Shopify Liquid outputted an error for incoming_inventory:', temp.error, 'for variant', variant.id);
              parsedIncomingInventory = [];
            } else {
              parsedIncomingInventory = temp;
            }
          } catch (e) {
            console.warn('Error parsing incoming_inventory for variant', variant.id, ':', e, 'Raw data:', incomingInventory);
            parsedIncomingInventory = [];
          }
        }
        window.incomingInventoryData[variant.id] = parsedIncomingInventory;

        let calculatedInventoryQuantity = variant.inventory_quantity || 0;
        if (parsedInventoryByLocation && Object.keys(parsedInventoryByLocation).length > 0) {
          calculatedInventoryQuantity = Object.values(parsedInventoryByLocation).reduce((sum, qty) => sum + qty, 0);
        }

        const productEntry = {
          id: product.id,
          title: product.title,
          handle: product.handle,
          url: `/products/${product.handle}?variant=${variant.id}`,
          sku: variant.sku || 'N/A',
          price: variant.price,
          compare_at_price: variant.compare_at_price,
          inventory_quantity: calculatedInventoryQuantity,
          inventory_policy: variant.inventory_policy || (variant.available ? 'continue' : 'deny'),
          inventory_management: variant.inventory_management,
          available: variant.available,
          image: (() => {
            if (variant.image) {
              return variant.image.src;
            }
            if (product.images && product.images.length > 0) {
              const sku = variant.sku ? variant.sku.toLowerCase() : '';
              if (sku) {
                const matchedImage = product.images.find(imgUrl => imgUrl.toLowerCase().includes(sku));
                if (matchedImage) {
                  return matchedImage;
                }
              }
              return product.images[0];
            }
            return null;
          })(),
          variant_id: variant.id,
          collections: product.product_type ? [product.product_type.toLowerCase().replace(/\s+/g, '-')] : [],
          tags: product.tags || [],
          created_at: new Date(product.created_at).getTime() / 1000,
          has_multiple_variants: product.options.size > 0 ,
          variant_options: variantOptions || null,
          custom_fields: {
            flat_rate_shipping: flatRateShipping,
            backorder_date: backorderDate,
            inventory_by_location: inventoryByLocation,
            incoming_inventory: incomingInventory,
            renegade_product: renegadeProduct
          }
        };
        
        // Always add to productEntries - filtering happens at the search level
        productEntries.push(productEntry);
      }
      return productEntries;
    } catch (error) {
      console.warn('Error fetching product details for handle:', handle, error);
      return [];
    }
  }

  performLocalSearch() {
    const searchTermLower = this.searchTerm.toLowerCase().trim();
    const results = this.allProducts.filter(product => {
      if (this.shouldHideRenegadeProduct(product)) {
        return false;
      }
      
      const productSkuLower = product.sku ? product.sku.toLowerCase().trim() : "";
      return product.title.toLowerCase().includes(searchTermLower) ||
         productSkuLower === searchTermLower;
    });
    this.displaySearchResults(results);
  }

  displaySearchResults(results) {
    this.filteredProducts = results;
    this.displayedProductCount = this.productsPerPage;
    this.renderCurrentPage();
    this.renderPagination();
    this.updateCollectionCountsDisplay();
    this.showSearchSuggestions(results.slice(0, 10));
  }

  showSearchSuggestions(suggestions) {
    const suggestionsContainer = document.getElementById('searchSuggestions');
    if (suggestions.length === 0) {
      suggestionsContainer.style.display = 'none';
      return;
    }
    let html = '';
    suggestions.forEach(product => {
      html += `
        <div class="search-suggestion" onclick="bulkOrderForm.selectSearchSuggestion('${product.variant_id}')">
          ${product.image ? `<img src="${product.image}" alt="${product.title}" class="product-image">` : '<div class="product-image" style="background:#f0f0f0;"></div>'}
          <div class="suggestion-info">
            <div class="suggestion-title">${product.title}</div>
            <div class="suggestion-sku">SKU: ${product.sku}</div>
          </div>
        </div>
      `;
    });
    suggestionsContainer.innerHTML = html;
    suggestionsContainer.style.display = 'block';
  }

  selectSearchSuggestion(variantId) {
    const product = this.productCache.get(parseInt(variantId)) ||
      this.allProducts.find(p => p.variant_id == variantId) ||
      this.filteredProducts.find(p => p.variant_id == variantId);
    if (product) {
      this.filteredProducts = [product];
      this.displayedProductCount = this.productsPerPage;
      this.renderCurrentPage();
      this.renderPagination();
      this.updateCollectionCountsDisplay();
      const searchTermLower = this.searchTerm.toLowerCase().trim();
      const productSkuLower = product.sku ? product.sku.toLowerCase().trim() : "";
      if (productSkuLower === searchTermLower) {
        document.getElementById('productSearch').value = product.sku;
      } else {
        document.getElementById('productSearch').value = product.title;
      }
      document.getElementById('searchSuggestions').style.display = 'none';
    }
  }

  clearSearch() {
    this.searchTerm = '';
    this.isSearchMode = false;
    document.getElementById('productSearch').value = '';
    this.applyFiltersAndSort();
    this.renderCurrentPage();
    this.renderPagination();
    this.updateCollectionCountsDisplay();
    document.getElementById('searchSuggestions').style.display = 'none';
  }

  async loadMoreProducts(startShopifyPage) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.showPageLoading();
    try {
      const requests = [];
      let pagesFetchedInThisCall = 0;
      for (let i = 0; i < 2; i++) {
        const page = startShopifyPage + i;
        if (page > 250) {
          console.warn(`Reached Shopify API page limit (250). Cannot load page ${page}.`);
          break;
        }
        requests.push(
          fetch(`/collections/all/products.json?limit=250&page=${page}`)
          .then(response => response.json())
        );
        pagesFetchedInThisCall++;
      }
      const results = await Promise.all(requests);
      let newProductsAddedCount = 0;
      let productsReturnedByAPI = 0;
      const productHandlesToFetch = [];
      for (const data of results) {
        if (data.products && data.products.length > 0) {
          productsReturnedByAPI += data.products.length;
          const products = data.products.filter(p => !p.title.toLowerCase().includes('sample'));
          products.forEach(p => productHandlesToFetch.push(p.handle));
        }
      }

      const fullProductDataPromises = productHandlesToFetch.map(handle =>
        this.fetchProductWithFullData(handle, false)
      );
      const allFullProductDataResults = await Promise.all(fullProductDataPromises);
      allFullProductDataResults.forEach(fullProductData => {
        fullProductData.forEach(productEntry => {
          if (!this.productCache.has(productEntry.variant_id)) {
            this.allProducts.push(productEntry);
            this.productCache.set(productEntry.variant_id, productEntry);
            newProductsAddedCount++;
          } else {
            this.productCache.set(productEntry.variant_id, productEntry);
            const indexInAllProducts = this.allProducts.findIndex(p => p.variant_id === productEntry.variant_id);
            if (indexInAllProducts !== -1) {
              this.allProducts[indexInAllProducts] = productEntry;
            }
          }
        });
      });

      if (productsReturnedByAPI > 0) {
        this.maxLoadedShopifyPage = Math.max(this.maxLoadedShopifyPage, startShopifyPage + pagesFetchedInThisCall - 1);
      }
    } catch (error) {
      console.error(`Error loading Shopify API pages starting from ${startShopifyPage}:`, error);
    }
    this.isLoading = false;
    this.hidePageLoading();
  }

  updateCollectionFilters() {
    const checkboxes = document.querySelectorAll('input[name="collection"]:checked');
    this.selectedCollections = Array.from(checkboxes).map(cb => cb.value);
  }

  updateAvailabilityFilters() {
    const checkboxes = document.querySelectorAll('input[name="availability"]:checked');
    this.selectedAvailability = Array.from(checkboxes).map(cb => cb.value);
  }

  applyFiltersAndSort() {
    if (this.isSearchMode) {
      return;
    }
    this.filteredProducts = this.allProducts.filter(product => {
      if (this.shouldHideRenegadeProduct(product)) {
        return false;
      }
      
      if (!this.selectedCollections.includes('all')) {
        const hasMatchingCollection = product.collections.some(collection =>
          this.selectedCollections.includes(collection)
        );
        if (!hasMatchingCollection) {
          return false;
        }
      }

      if (this.selectedAvailability.length > 0) {
        const status = this.getInventoryStatus(product);
        if (!this.selectedAvailability.includes(status)) {
          return false;
        }
      }
      return true;
    });

    this.displayedProductCount = this.productsPerPage;
  }

  getInventoryStatus(product) {
    let status = '';
    if (product.inventory_quantity > 0) {
      status = 'in-stock';
    } else if (product.inventory_policy === 'continue' && product.available) {
      status = product.tags && product.tags.includes('preorder') ? 'preorder' : 'backorder';
    } else {
      status = 'out-of-stock';
    }
    return status;
  }

  renderCurrentPage() {
    const pageProducts = this.filteredProducts.slice(0, this.displayedProductCount);
    const tbody = document.getElementById('productsTableBody');
    let html = '';
    if (pageProducts.length === 0 && this.filteredProducts.length === 0 && !this.isLoading) {
      html = `<tr><td colspan="8" style="text-align: center; padding: 20px;">No products found matching your criteria.</td></tr>`;
    } else {
      pageProducts.forEach(product => {
        html += this.renderProductRow(product);
        html += this.renderInventoryDetailsRow(product);
      });
    }
    tbody.innerHTML = html;
    this.updateResultsInfo();
  }

  renderProductRow(product) {
    const status = this.getInventoryStatus(product);
    const statusLabels = {
      'in-stock': 'In Stock',
      'backorder': 'Pre-order',
      'preorder': 'Pre-order',
      'out-of-stock': 'Out of Stock'
    };

    const isTradeCustomer = product.custom_fields.is_trade_customer;

    const flatRateShipping = product.custom_fields.flat_rate_shipping ?
      (typeof product.custom_fields.flat_rate_shipping === 'number' ?
        `$${parseFloat(product.custom_fields.flat_rate_shipping).toFixed(0)} per item` :
        `$${product.custom_fields.flat_rate_shipping} per item`) :
      'N/A';


    let inventoryDisplay = '';
    if (product.inventory_management) {
      inventoryDisplay = product.inventory_quantity > 0 ? product.inventory_quantity.toString() : '';
    } else {
      inventoryDisplay = 'Unlimited';
    }

    const hasInventoryDetails = product.custom_fields.inventory_by_location || product.custom_fields.incoming_inventory;
    const inventoryDetails = [];
    if (product.custom_fields.backorder_date && status === 'backorder') {
      inventoryDetails.push(`Expected: ${product.custom_fields.backorder_date}`);
    }

    return `
      <tr data-product-id="${product.id}" data-variant-id="${product.variant_id}">
        <td>
          ${product.image ? `<img src="${product.image}" alt="${product.title}" class="product-image">` : '<div class="product-image" style="background:#f0f0f0;"></div>'}
        </td>
        <td>
          <div class="product-name">
            <a href="${product.url}" target="_blank">${product.title}</a>
          </div>
        </td>
        <td>
          <div class="product-sku">${product.sku}</div>
          ${product.variant_options ? `<div class="variant-options">${product.variant_options}</div>` : ''}
        </td>
        <td>
          <div class="product-price">$${(product.price / 100).toFixed(2)}</div>
        </td>
        <td>
          <div class="inventory-status">
            <div class="inventory-main">
              <span class="status-badge ${status}">${statusLabels[status]}</span>
              <div class="inventory-display">
                <span class="inventory-quantity">${inventoryDisplay}</span>
                ${hasInventoryDetails ? `
                  <button
                    class="inventory-toggle-btn"
                    onclick="bulkOrderForm.toggleInventoryDetails('${product.variant_id}', event)"
                    aria-label="Show inventory details"
                  >
                    +
                  </button>
                ` : ''}
              </div>
            </div>
            ${inventoryDetails.length > 0 ? `<div class="inventory-details">${inventoryDetails.join('<br>')}</div>` : ''}
          </div>
        </td>
        ${
          isTradeCustomer ? `` : `
            <td>
              <div class="flat-rate-shipping">
                ${flatRateShipping}
                <span class="info-icon">      <svg xmlns="http://www.w3.org/2000/svg" class="info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg></span>
                <span class="tooltip">
                  This is the most you’ll pay for shipping. If the actual cost is lower, we’ll automatically charge you less. For large orders, contact us for an exact shipping quote.
                </span>
              </div>
            </td>          
          `
        }
        <td>
          <div class="quantity-controls">
            <button class="quantity-btn" onclick="this.parentNode.querySelector('.quantity-input').stepDown()">-</button>
            <input type="number" class="quantity-input" value="1" min="1" max="999">
            <button class="quantity-btn" onclick="this.parentNode.querySelector('.quantity-input').stepUp()">+</button>
          </div>
        </td>
        <td>
          <button class="add-to-cart-btn" onclick="bulkOrderForm.addToCart(${product.variant_id}, this)" ${!product.available ? 'disabled' : ''}>
            Add to cart
          </button>
        </td>
      </tr>
    `;
  }

  renderInventoryDetailsRow(product) {
    const hasInventoryDetails = product.custom_fields.inventory_by_location || product.custom_fields.incoming_inventory;
    if (!hasInventoryDetails) {
      return '';
    }
    return `
      <tr class="inventory-details-row" id="inventory-details-row-${product.variant_id}">
        <td colspan="8" class="inventory-details-cell">
          <div class="inventory-details-content">
            <div class="inventory-section">
              <div class="inventory-section-title">AVAILABLE INVENTORY</div>
              <table class="inventory-table available-inventory">
                <thead>
                  <tr>
                    <th>WAREHOUSE</th>
                    <th>Qty</th>
                  </tr>
                </thead>
                <tbody id="current-inventory-body-${product.variant_id}">
                </tbody>
              </table>
            </div>
            <div class="inventory-section">
              <div class="inventory-section-title">INCOMING INVENTORY</div>
              <table class="inventory-table incoming-inventory">
                <thead>
                  <tr>
                    <th>LOCATION</th>
                    <th>Qty</th>
                    <th>ETA</th>
                  </tr>
                </thead>
                <tbody id="incoming-inventory-body-${product.variant_id}">
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    `;
  }

  toggleInventoryDetails(variantId, event) {
    if (event) {
      event.stopPropagation();
    }
    const row = document.getElementById('inventory-details-row-' + variantId);
    const button = event ? event.target : document.querySelector(`[onclick*="${variantId}"]`);
    const isVisible = row && row.classList.contains('show');

    document.querySelectorAll('.inventory-details-row.show').forEach(openRow => {
      if (openRow.id !== 'inventory-details-row-' + variantId) {
        openRow.classList.remove('show');
      }
    });

    document.querySelectorAll('.inventory-toggle-btn.active').forEach(activeBtn => {
      if (activeBtn !== button) {
        activeBtn.classList.remove('active');
        activeBtn.textContent = '+';
      }
    });

    if (!isVisible && row) {
      row.classList.add('show');
      if (button) {
        button.classList.add('active');
        button.textContent = '−';
      }
      this.populateCurrentInventory(variantId);
      this.populateIncomingInventory(variantId);
    } else if (row) {
      row.classList.remove('show');
      if (button) {
        button.classList.remove('active');
        button.textContent = '+';
      }
    }
  }

  populateCurrentInventory(variantId) {
    const data = window.inventoryDetailsData[variantId];
    const tbody = document.getElementById('current-inventory-body-' + variantId);
    let html = '';
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      Object.keys(data).forEach(warehouseName => {
        const quantity = data[warehouseName];
        html += `<tr>
          <td>${warehouseName}</td>
          <td>${quantity}</td>
        </tr>`;
      });
    }
    if (html === '') {
      html = '<tr><td colspan="2">No current inventory available</td></tr>';
    }
    if (tbody) tbody.innerHTML = html;
  }

  populateIncomingInventory(variantId) {
    const data = window.incomingInventoryData[variantId];
    const tbody = document.getElementById('incoming-inventory-body-' + variantId);
    let html = '';
    if (Array.isArray(data) && data.length > 0) {
      data.forEach(item => {
        html += `<tr>
          <td>${item.Location || 'Unknown'}</td>
          <td>${item['Future Availability'] || 0}</td>
          <td>${item.ETA || 'TBD'}</td>
        </tr>`;
      });
    }
    if (html === '') {
      html = '<tr><td colspan="3">No incoming inventory scheduled</td></tr>';
    }
    if (tbody) tbody.innerHTML = html;
  }

  renderPagination() {
    const pagination = document.getElementById('pagination');
    const hasMoreProducts = this.displayedProductCount < this.filteredProducts.length;
    if (hasMoreProducts) {
      pagination.innerHTML = `<button id="loadMoreBtn" onclick="bulkOrderForm.loadMore()">Load More</button>`;
    } else {
      pagination.innerHTML = '';
    }
  }

  async loadMore() {
    this.showPageLoading();
    const productsToIncrementDisplay = 2 * this.productsPerPage;
    const newTargetDisplayedCount = this.displayedProductCount + productsToIncrementDisplay;

    if (newTargetDisplayedCount > this.filteredProducts.length) {
      const nextShopifyPageToFetch = this.maxLoadedShopifyPage + 1;
      await this.loadMoreProducts(nextShopifyPageToFetch);
      this.applyFiltersAndSort();
    }

    this.displayedProductCount = Math.min(newTargetDisplayedCount, this.filteredProducts.length);
    this.renderCurrentPage();
    this.renderPagination();
    this.updateCollectionCountsDisplay();
    this.hidePageLoading();
  }

  updateResultsInfo() {
    const start = Math.min(1, this.filteredProducts.slice(0, this.displayedProductCount).length);
    const end = this.filteredProducts.slice(0, this.displayedProductCount).length;
    const total = this.filteredProducts.length;
    let infoText = '';
    if (total === 0) {
      infoText = 'No products found.';
    } else {
      infoText = `Showing ${start}-${end}`;
    }
    document.getElementById('resultsInfo').textContent = infoText;
  }

  updateCollectionCountsDisplay() {
    const collectionCountSpans = document.querySelectorAll('.collection-count');
    const allProducts = this.allProducts;
    collectionCountSpans.forEach(span => {
      const handle = span.dataset.collectionHandle;
      let count = 0;
      if (handle === 'all') {
        count = allProducts.length;
      } else {
        count = allProducts.filter(product => product.collections.includes(handle)).length;
      }
      span.textContent = `(${count})`;
    });
  }

  addToCart(variantId, button) {
    const quantityInput = button.parentNode.parentNode.querySelector('.quantity-input');
    const quantity = parseInt(quantityInput.value, 10);
    if (isNaN(quantity) || quantity <= 0) {
      alert('Please enter a valid quantity.');
      return;
    }
    button.disabled = true;
    button.textContent = 'Adding...';
    fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity: quantity,
          id: variantId,
          sections:"cart-icon-bubble,cart-drawer"
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (data.sections && data.sections['cart-icon-bubble']) {
          const cartIconElement = document.querySelector('#cart-icon-bubble');
          if (cartIconElement) {
            cartIconElement.innerHTML = data.sections['cart-icon-bubble'];
          }
        }
        if (data.sections && data.sections['cart-drawer']) {
          const cartDrawerElement = document.querySelector('cart-drawer');
          if (cartDrawerElement) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data.sections['cart-drawer'], "text/html");
            const updatedCartDrawer = doc.body.querySelector("cart-drawer");
            if (updatedCartDrawer) {
              cartDrawerElement.replaceWith(updatedCartDrawer);
            }
          }
        }
        
        button.textContent = 'Added!';

        // Publish cart update so the header badge subscriber picks it up.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bulk-order-form',
            cartData: data,
          });
        }

        setTimeout(() => {
          button.disabled = false;
          button.textContent = 'Add to cart';
        }, 2000);
      })
      .catch(error => {
        console.error('Error:', error);
        alert('Failed to add to cart. Please try again.');
        button.disabled = false;
        button.textContent = 'Add to cart';
      });
  }

  showLoading() {
    document.getElementById('loadingState').style.display = 'block';
  }

  hideLoading() {
    document.getElementById('loadingState').style.display = 'none';
  }

  showPageLoading() {
    document.getElementById('pageLoading').style.display = 'block';
  }

  hidePageLoading() {
    document.getElementById('pageLoading').style.display = 'none';
  }
}

let bulkOrderForm;
document.addEventListener('DOMContentLoaded', () => {
  bulkOrderForm = new FinalB2BBulkOrderForm();
  window.bulkOrderForm = bulkOrderForm;
});
